import axios from "axios";
import { axiosInstance } from "./axiosInstance";

const RESOURCE_URL_V2 = process.env.REACT_APP_API_URL_V2 + "/admin/campus/";

export class PaymentService {
    config = (sessionToken: string | null, sendFiles: boolean = false) => {
        return {
            headers:
            {
                "Content-Type": sendFiles ? "multipart/form-data" : "application/json",
                Authorization: `Bearer ${sessionToken}`
            }
        };
    }

    isValidString = (value: string | null | undefined): boolean => {
        return typeof value === "string" && value.trim() !== "";
    }

    getPaginatedPayments = async (sessionToken: string | null, uri: string) => {
        let response = await axios.get(uri, this.config(sessionToken));
        return response;
    }

    getPaymentsByCampusId = async (sessionToken: string | null, campusId: number, perPage: number, criteria?: any, page?: number) => {
        let uri = `${RESOURCE_URL_V2}${campusId}/payments?perPage=${perPage}`;
		if (page && page > 1) { uri = `${uri}&page=${page}`; }   // página recordada (utils/paginaRecordada)
        if (criteria && typeof criteria === 'object' && Object.keys(criteria).length > 0) {
            try {
                const criteriaJson = JSON.stringify(criteria);
                uri = `${uri}&criteria=${encodeURIComponent(criteriaJson)}`;
            } catch (error) {
                console.error("Error serializando criteria as JSON:", error);
                throw new Error("Invalid criteria format");
            }
        }
        let response = await axios.get(uri, this.config(sessionToken));
        return response;
    }

    /* ---- Pagos y Movimientos (2026-09): el pago como lo ve el banco, con sus movimientos por alumno ---- */

    /* ---- Registrar pago (Cobros unificado, Etapa 1): el modal único de Caja ---- */

    /** Métodos y cuentas del plantel que recibe (misma regla que valida el servidor). digital=true: desde un ticket. */
    registerMethods = async (campusId: number, digital: boolean) =>
        axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/payments/_methods`, { params: digital ? { digital: 1 } : {} });

    /** Buscador de personas: alumnos del plantel (y de Online si cobra en la misma moneda). */
    registerPeople = async (campusId: number, q: string) =>
        axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/payments/_people`, { params: { q } });

    /** Todo lo que debe una persona: renglones cobrables + promociones vigentes de sus planteles. */
    registerOwed = async (campusId: number, studentId: number, intentId?: number | null) =>
        axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/payments/_owed/${studentId}`, { params: intentId ? { intentId } : {} });

    /** Cambio de promoción: si la persona toma esta promo en este renglón, qué promo reemplaza y qué meses se reabren (solo lectura). */
    promoChange = async (campusId: number, studentId: number, key: string, promotionId: number, intentId?: number | null) =>
        axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/payments/_promoChange`, { params: { studentId, key, promotionId, ...(intentId ? { intentId } : {}) } });

    /** Aplicar Promoción: promos de adelanto que se pueden aplicar a esta persona con este monto y fecha de pago (solo lectura). */
    promoOptions = async (campusId: number, params: { studentId: number; amount: number; paymentDate: string; intentId?: number | null; onlyDraft?: boolean }) =>
        axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/payments/_promoOptions`, { params: { studentId: params.studentId, amount: params.amount || 0, paymentDate: params.paymentDate,
            ...(params.intentId ? { intentId: params.intentId } : {}), ...(params.onlyDraft ? { onlyDraft: 1 } : {}) } });

    /** Aplicar Promoción sin cobrar: todo lo que pide ya está pagado a tiempo por otro camino (opción `paidAlready`). */
    promoApplyPaid = async (campusId: number, payload: { studentId: number; enrollmentId: number; promotionId: number; advisors: any[]; giftStudentId?: number; giftEnrollmentId?: number; giftOptionKey?: string; intentId?: number }) =>
        axiosInstance.post(`${RESOURCE_URL_V2}${campusId}/payments/_promoApplyPaid`, payload);

    /** 2x1 en Caja: matrículas de la persona (y búsqueda de otra), vista previa y registrar. */
    twoForOneOptions = async (campusId: number, studentId: number, q: string) =>
        axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/payments/_twoForOne/_options`, { params: { studentId, q } });
    twoForOnePreview = async (campusId: number, giftEnrollmentId: number, baseEnrollmentId: number) =>
        axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/payments/_twoForOne`, { params: { giftEnrollmentId, baseEnrollmentId } });
    twoForOneApply = async (campusId: number, giftEnrollmentId: number, baseEnrollmentId: number) =>
        axiosInstance.post(`${RESOURCE_URL_V2}${campusId}/payments/_twoForOne`, { giftEnrollmentId, baseEnrollmentId });

    /** Registra el pago (FormData por los comprobantes). */
    register = async (campusId: number, payload: any) => {
        const formData = new FormData();
        ["total", "paymentMethodId", "accountId", "paymentDate", "reference", "notes", "payerName", "payerPhone", "clientKey", "intentId", "intentStudentId"].forEach((k) => {
            formData.append(k, payload[k] === undefined || payload[k] === null ? "" : String(payload[k]));
        });
        (payload.attachedFiles || []).forEach((file: File, index: number) => formData.append(`attachedFiles[${index}]`, file));
        formData.append("persons", JSON.stringify(payload.persons));
        formData.append("promoChanges", JSON.stringify(payload.promoChanges || []));
        if (payload.promoApplication) formData.append("promoApplication", JSON.stringify(payload.promoApplication));
        if (Array.isArray(payload.ticketDocs)) formData.append("ticketDocs", JSON.stringify(payload.ticketDocs));
        // Sin promoción (2026-11-02): la cajera dijo que este pago no lleva la promo que se aplicaría sola o que menciona la nota.
        if (payload.promoAuto === false) { formData.append("promoAuto", "0"); formData.append("promoAutoReason", String(payload.promoAutoReason || "")); }
        return axiosInstance.post(`${RESOURCE_URL_V2}${campusId}/payments/_register`, formData, { headers: { "Content-Type": "multipart/form-data" } });
    }

    /** Lee una foto adjunta directo (sin ticket): prellenado, avisos y si ya está en otro pago (2026-11-02). */
    readVoucher = (campusId: number, file: File) => {
        const formData = new FormData();
        formData.append("file", file);
        return axiosInstance.post(`${RESOURCE_URL_V2}${campusId}/payments/_readVoucher`, formData, { headers: { "Content-Type": "multipart/form-data" } });
    }

    discountRequests = async (campusId: number, statusId?: number) =>
        axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/payments/_discountRequests`, { params: statusId ? { statusId } : {} });

    requestDiscount = async (campusId: number, payload: { studentId: number; rowKey: string; calcType: "percent" | "amount"; value: number; reason: string; intentId?: number | null }) =>
        axiosInstance.post(`${RESOURCE_URL_V2}${campusId}/payments/_discountRequests`, payload);

    decideDiscount = async (campusId: number, requestId: number, accept: boolean, notes: string) =>
        axiosInstance.post(`${RESOURCE_URL_V2}${campusId}/payments/_discountRequests/${requestId}/_decide`, { accept, notes });

    /** Recargo automático (Etapa 2): Supervisión de Caja lo exime (total o parcial); la cajera lo sugiere. */
    waiveSurcharge = async (campusId: number, surchargeId: number, payload: { calcType: "percent" | "amount"; value: number; reason: string }) =>
        axiosInstance.post(`${RESOURCE_URL_V2}${campusId}/payments/_surcharges/${surchargeId}/_waive`, payload);

    requestSurchargeWaiver = async (campusId: number, surchargeId: number, payload: { calcType: "percent" | "amount"; value: number; reason: string; intentId?: number | null }) =>
        axiosInstance.post(`${RESOURCE_URL_V2}${campusId}/payments/_surcharges/${surchargeId}/_waiverRequest`, payload);

    surchargeHistory = async (campusId: number, surchargeId: number) =>
        axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/payments/_surcharges/${surchargeId}/_history`);

    /** Pantalla Recargos (solo superadmin): plantel → curso → horario. */
    surchargeRules = async (campusId: number) =>
        axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/surchargeRules`);

    saveSurchargeRule = async (campusId: number, payload: any) =>
        axiosInstance.post(`${RESOURCE_URL_V2}${campusId}/surchargeRules`, payload);

    deleteSurchargeRule = async (campusId: number, ruleId: number) =>
        axiosInstance.delete(`${RESOURCE_URL_V2}${campusId}/surchargeRules/${ruleId}`);

    previewSurchargeSteps = async (campusId: number, steps: any[]) =>
        axiosInstance.post(`${RESOURCE_URL_V2}${campusId}/surchargeRules/_preview`, { steps });

    /** Catálogo de Promos (antes Descuentos, Etapa 3): catálogo del plantel y acuerdos comerciales por matrícula. */
    promotions = async (campusId: number) =>
        axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/promotions`);

    savePromotion = async (campusId: number, payload: any) => payload.id
        ? axiosInstance.put(`${RESOURCE_URL_V2}${campusId}/promotions/${payload.id}`, payload)
        : axiosInstance.post(`${RESOURCE_URL_V2}${campusId}/promotions`, payload);

    /* Promos flexibles: reporte de usos y copiar a otros planteles (los cursos reales vienen con el catálogo). */
    promotionReport = async (campusId: number, from: string, to: string) =>
        axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/promotions/_report?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);

    copyPromotion = async (campusId: number, promotionId: number, campusIds: number[]) =>
        axiosInstance.post(`${RESOURCE_URL_V2}${campusId}/promotions/${promotionId}/_copy`, { campusIds });

    promotionHistory = async (campusId: number, promotionId: number) =>
        axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/promotions/${promotionId}/_history`);

    /** Acuerdos por matrícula (hoy en Promociones): el servidor decide el alcance (la asesora, solo los suyos). */
    agreements = async (campusId: number, filters: { status?: string; q?: string; page?: number; perPage?: number }) =>
        axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/promotions/_agreements?status=${encodeURIComponent(filters.status || "")}&q=${encodeURIComponent(filters.q || "")}&page=${filters.page || 1}${filters.perPage ? "&perPage=" + filters.perPage : ""}`);

    agreement = async (campusId: number, agreementId: number) =>
        axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/promotions/_agreements/${agreementId}`);

    cancelAgreement = async (campusId: number, agreementId: number, reason: string) =>
        axiosInstance.put(`${RESOURCE_URL_V2}${campusId}/promotions/_agreements/${agreementId}/_cancel`, { reason });

    /* Etapa 4: Descuentos › Grupos «Por resolver» y Auditoría. */
    discountGroups = async (campusId: number, status: string = "abiertos") =>
        axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/promotions/_groups?status=${encodeURIComponent(status)}`);

    discountGroupsCount = async (campusId: number) =>
        axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/promotions/_groups/_count`);

    resolveGroupIssue = async (campusId: number, issueId: number, resolution: string, notes: string) =>
        axiosInstance.put(`${RESOURCE_URL_V2}${campusId}/promotions/_groups/_issues/${issueId}/_resolve`, { resolution, notes });

    /* Etapa 5: Descuentos › Limpieza (matrículas activas sin acuerdo: lo que WAPP infiere de lo pagado). */
    cleanup = async (campusId: number, f: { status?: string; confidence?: string; pattern?: string; flag?: boolean; q?: string; aiConfidence?: string; aiMatch?: string; certeza?: string; orden?: string; studentId?: number; enrollmentId?: number }, page: number = 1) =>
        axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/promotions/_cleanup?page=${page}&perPage=100&status=${encodeURIComponent(f.status ?? "1")}&confidence=${encodeURIComponent(f.confidence || "")}`
            + `&pattern=${encodeURIComponent(f.pattern || "")}&flag=${f.flag ? 1 : ""}&q=${encodeURIComponent(f.q || "")}`
            + `&aiConfidence=${encodeURIComponent(f.aiConfidence || "")}&aiMatch=${encodeURIComponent(f.aiMatch || "")}`
            + `&certeza=${encodeURIComponent(f.certeza || "")}&orden=${encodeURIComponent(f.orden || "")}`
            + (f.studentId ? `&studentId=${f.studentId}` : "") + (f.enrollmentId ? `&enrollmentId=${f.enrollmentId}` : ""));

    /** Matrículas de la persona con la Limpieza sin hacer (sus mensualidades no se cobran hasta decidir): { rows: [{ cleanupId, campusId, enrollmentId, courseName, name, canDecide }] }. */
    cleanupPending = async (campusId: number, studentId: number) =>
        axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/promotions/_cleanup/_pending?studentId=${studentId}`);

    /* Notas (perfil, matrícula, solicitud, pagos, facturas) y estado de cuenta del alumno del caso. Solo lectura. */
    cleanupFile = async (campusId: number, cleanupId: number) =>
        axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/promotions/_cleanup/${cleanupId}/_file`);

    cleanupItem = async (campusId: number, cleanupId: number, promotionId?: number | null) =>
        axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/promotions/_cleanup/${cleanupId}${promotionId ? "?promotionId=" + promotionId : ""}`);

    /* «¿Con quién va?» de una promo ligada: sugerencias, búsqueda y lo que no cuadra con las reglas para lo elegido. */
    cleanupLinks = async (campusId: number, cleanupId: number, q: string, promotionId: number, selected: number[], alReves = false) =>
        axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/promotions/_cleanup/${cleanupId}/_links?q=${encodeURIComponent(q)}&promotionId=${promotionId}&selected=${selected.join(",")}${alReves ? "&alReves=1" : ""}`);

    cleanupDecide = async (campusId: number, cleanupId: number, payload: { decision: string; promotionId?: number | null; notes?: string; permitirSubir?: boolean; links?: number[]; alReves?: boolean }) =>
        axiosInstance.put(`${RESOURCE_URL_V2}${campusId}/promotions/_cleanup/${cleanupId}/_decide`, payload);

    cleanupRefresh = async (campusId: number) =>
        axiosInstance.put(`${RESOURCE_URL_V2}${campusId}/promotions/_cleanup/_refresh`, {});

    /** ¿Sale la pestaña Limpieza en Promociones? { canSee, canDecide, pending }. */
    cleanupAccess = async (campusId: number) =>
        axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/promotions/_cleanup/_access`);

    audits = async (campusId: number, filters: { category: string; status?: string; q?: string }) =>
        axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/audits?category=${encodeURIComponent(filters.category)}&status=${encodeURIComponent(filters.status || "pendientes")}&q=${encodeURIComponent(filters.q || "")}`);

    auditCounts = async (campusId: number) =>
        axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/audits/_counts`);

    audit = async (campusId: number, auditId: number) =>
        axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/audits/${auditId}`);

    reviewAudit = async (campusId: number, auditId: number, statusId: number, notes: string) =>
        axiosInstance.put(`${RESOURCE_URL_V2}${campusId}/audits/${auditId}/_review`, { statusId, notes });

    /* Preguntar y explicar (ESPEC_AUDITORIA_PREGUNTAS C): Auditoría pregunta; la persona (o su supervisión) responde. */
    askAudit = async (campusId: number, auditId: number, text: string) =>
        axiosInstance.put(`${RESOURCE_URL_V2}${campusId}/audits/${auditId}/_ask`, { text });

    auditQuestions = async (campusId: number, status: string) =>
        axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/auditQuestions?status=${encodeURIComponent(status)}`);

    auditQuestionsCount = async (campusId: number) =>
        axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/auditQuestions/_count`);

    auditQuestion = async (campusId: number, auditId: number) =>
        axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/auditQuestions/${auditId}`);

    answerAudit = async (campusId: number, auditId: number, text: string) =>
        axiosInstance.put(`${RESOURCE_URL_V2}${campusId}/auditQuestions/${auditId}/_answer`, { text });

    /* «Aprobar de todos modos» un multimes / 2x1 que ya no puede cumplirse: desde Registrar pago o desde Descuentos › Acuerdos. */
    approveAdvanceException = async (campusId: number, agreementId: number, reason: string, desde: "payments" | "promotions" = "payments") =>
        axiosInstance.put(`${RESOURCE_URL_V2}${campusId}/${desde === "payments" ? "payments" : "promotions"}/_agreements/${agreementId}/_approveException`, { reason });

    checkReference = async (campusId: number, params: { accountId: number; paymentMethodId: number; reference: string; paymentDate: string; total: number }) =>
        axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/payments/_referenceCheck`, { params });

    getReceiptDetails = async (campusId: number, receiptId: number) =>
        axiosInstance.get(`${RESOURCE_URL_V2}${campusId}/payments/${receiptId}/_receiptDetails`);

    cancelReceipt = async (campusId: number, receiptId: number, notes: string) =>
        axiosInstance.post(`${RESOURCE_URL_V2}${campusId}/payments/${receiptId}/_receiptCancel`, { notes });

    getPaymentById = async (sessionToken: string | null, campusId: number, paymentId: number) => {
        let uri = `${RESOURCE_URL_V2}${campusId}/payments/${paymentId}/_details`;
        let response = await axios.get(uri, this.config(sessionToken));
        return response;
    }

    cancelPaymentById = async (sessionToken: string | null, campusId: number, paymentId: number, payload: any) => {
        let uri = `${RESOURCE_URL_V2}${campusId}/payments/${paymentId}/_cancel`;
        let response = await axios.post(uri, payload, this.config(sessionToken));
        return response;
    }

    payOwedItems = async (sessionToken: string | null, campusId: number, studentId: number, payload: any) => {
        let uri = `${RESOURCE_URL_V2}${campusId}/students/${studentId}/payments`;
        const formData = new FormData();

        formData.append("paidAmount", payload.paidAmount);
        formData.append("paymentDate", payload.paymentDate);
        formData.append("paymentMethodId", payload.paymentMethodId);
        formData.append("accountId", payload.accountId);
        formData.append("reference", payload.reference || "");
        formData.append("notes", payload.notes || "");
        payload.attachedFiles.forEach((file: File, index: number) => {
            formData.append(`attachedFiles[${index}]`, file);
        });
        formData.append("invoiceItems", JSON.stringify(payload.invoiceItems));

        let response = await axios.post(uri, formData, this.config(sessionToken, true));
        return response;
    }

    addPaymentDocument = async (campusId: number, paymentId: number, payload: any) => {
        let uri = `${RESOURCE_URL_V2}${campusId}/payments/${paymentId}/_update`;
        const formData = new FormData();
        payload.attachedFiles.forEach((file: File, index: number) => {
            formData.append(`attachedFiles[${index}]`, file);
        });

        let response = await axiosInstance.post(
            uri,
            formData,
            { headers: { "Content-Type": "multipart/form-data" } }
        );
        return response;
    }
}